"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import VideoModal from "../ui/VideoModal";
import StickyPiP from "../ui/StickyPiP";
import DemoTag from "../ui/DemoTag";

interface VideoExperienceContextType {
    isModalOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    isPiPVisible: boolean;
    setPiPVisibility: (visible: boolean) => void;
    isPiPHidden: boolean;
    hidePiPPermanent: () => void;
    restorePiP: () => void;
}

const VideoExperienceContext = createContext<VideoExperienceContextType | undefined>(undefined);

const BLACKLIST = ["/privacy", "/terms", "/faq", "/cookie-policy", "/wavault-goodbye"];

export default function VideoExperienceProvider({ children }: { children: React.ReactNode }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPiPVisible, setIsPiPVisible] = useState(false);
    const [isPiPHidden, setIsPiPHidden] = useState(false);
    const pathname = usePathname();

    const isBlacklisted = BLACKLIST.some(path => pathname.startsWith(path));

    // Logic: On home, PiP visibility is controlled by the VideoShowcase intersection in HomeClient.
    // On other pages (guides), we use a "Respectful Hook" (Scroll-based or Delayed Fallback).
    useEffect(() => {
        if (pathname !== "/") {
            let triggered = false;

            const triggerPiP = () => {
                if (triggered) return;
                triggered = true;
                setPiPVisibility(true);
            };

            // Fallback: 5 seconds after load
            const timer = setTimeout(triggerPiP, 5000);

            // Interaction: Scroll intent (300px threshold)
            const handleScroll = () => {
                if (window.scrollY > 300) {
                    triggerPiP();
                    window.removeEventListener("scroll", handleScroll);
                }
            };

            window.addEventListener("scroll", handleScroll);
            
            return () => {
                clearTimeout(timer);
                window.removeEventListener("scroll", handleScroll);
            };
        } else {
            // Home page: visibility is managed externally (HomeClient.tsx)
            // But we reset on mount to avoid leaks
            setPiPVisibility(false);
        }
    }, [pathname]);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);
    const setPiPVisibility = (visible: boolean) => setIsPiPVisible(visible);
    const hidePiPPermanent = () => {
        setIsPiPHidden(true);
        setIsPiPVisible(false);
    };
    const restorePiP = () => {
        setIsPiPHidden(false);
        setIsPiPVisible(true);
    };

    const value = {
        isModalOpen,
        openModal,
        closeModal,
        isPiPVisible: isPiPVisible && !isPiPHidden && !isBlacklisted,
        setPiPVisibility,
        isPiPHidden,
        hidePiPPermanent,
        restorePiP,
    };

    return (
        <VideoExperienceContext.Provider value={value}>
            {children}
            
            {/* Global Video UI Elements */}
            <VideoModal 
                isOpen={isModalOpen} 
                onClose={closeModal} 
                videoSrc="/demo.mp4"
            />
            
            <StickyPiP 
                isVisible={value.isPiPVisible}
                onClose={hidePiPPermanent}
                onExpand={openModal}
                videoSrc="/demo.mp4"
            />

            <DemoTag 
                isVisible={isPiPHidden && !isBlacklisted} 
                onClick={restorePiP}
            />
        </VideoExperienceContext.Provider>
    );
}

export function useVideoExperience() {
    const context = useContext(VideoExperienceContext);
    if (!context) {
        throw new Error("useVideoExperience must be used within a VideoExperienceProvider");
    }
    return context;
}
